/**
 * Settings — this door phone's own preferences: which ticket types and which
 * ticket provenance this entrance currently admits, whether a scan writes on
 * decode (auto) or
 * withholds until an explicit tap (manual), and whether a verdict is
 * announced by tone/vibration. Every field earned its place against a
 * named failure: the type filter exists because one venue runs several
 * doors with different entitlements and a General Admission ticket at the
 * VIP entrance must render wrong-type rather than being redeemed;
 * auto/manual exists because auto mode has no undo and a stray decode
 * destroys data; sound/haptics are each a strict on/off pair, never a
 * volume slider — the prior system's admit tone drifted to an inaudible
 * 0.1 through exactly that lever, and its rejection tone was silent
 * outright. Pure state — no `.sync.ts` reconciler: nothing here has a
 * remote backing to reconcile against.
 *
 * The type filter is a special case among these fields: its values are
 * per-event Firestore auto-ids, not a device-wide preference like the
 * others, so it also carries `typeFilterEventId` naming which event it was
 * set for — read together as `acceptedTypeIds`, the gated value every
 * caller actually uses — and `typeFilterPickedAt`, the instant an operator
 * took this door off automatic. See those cells' own comments below.
 *
 * Every field is `.local()` + `keepOnClear: true`. These are properties of
 * the physical door position — one check-in account IS one door phone
 * (`ticket.entity.ts`'s own `redeemedBy` field comment) — not of the
 * signed-in identity, so a shift-change sign-in on the SAME phone
 * (`session.sync.ts`'s own `clearStates()` case) must not silently revert
 * what this entrance accepts, which mode it redeems in, or how it warns.
 * `.local()` also means a phone closed and reopened mid-shift keeps its
 * configuration — the type filter and manual-mode brake are exactly the
 * settings a reload must not silently undo.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { type Event, type Ticket, TicketType } from "@momo/types";
import {
	combineLatest,
	defer,
	map,
	repeat,
	startWith,
	switchMap,
	timer,
} from "rxjs";
import { z } from "zod";
import {
	eventDetailsById,
	selectedEventId,
	venueTimezone,
} from "../event/event.state.ts";
import { ticketTypesIndex } from "../ticket-types/ticket-types.state.ts";
import { todayInstant } from "./today.state.ts";

export type CheckinMode = "auto" | "manual";

/** Which provenance this entrance admits. `all` is the unfiltered default —
 *  the same open-by-default polarity the type filter carries, spelled as a
 *  value rather than as an empty collection. */
export type OriginFilter = "all" | "guest" | "sale";

export const checkinMode = state<CheckinMode>("scanner/checkin-mode").local(
	"auto",
	{ keepOnClear: true },
);

export const soundEnabled = state<boolean>("scanner/sound-enabled").local(
	true,
	{ keepOnClear: true },
);

export const hapticsEnabled = state<boolean>("scanner/haptics-enabled").local(
	true,
	{ keepOnClear: true },
);

// The typeIds THIS entrance currently admits. Empty means no filter has
// been configured yet — a freshly provisioned door must scan, not silently
// reject everything, so what an unconfigured door admits is decided one
// level up, by `defaultAcceptedTypeIds`: the types whose own entry window
// contains this instant where the catalog and the schedule resolve, and every
// type where they do not. The ids themselves
// are Firestore auto-ids from one event's own ticket-type catalog — unique to
// that event, meaningless against any other — so this value alone never says
// whether it still applies; `typeFilterEventId` below names the event it was
// set for, and `acceptedTypeIds` is the gated read every caller uses instead
// of this raw value.
export const typeFilter = state<readonly string[]>("scanner/type-filter").local(
	[],
	{ keepOnClear: true },
);

// Which event `typeFilter` was configured against. A brand-new namespace
// (not a reshape of `typeFilter` itself) precisely so a value already on
// disk from before this field existed decodes safely: it was never written,
// so it loads back at its own default, `null` — read below as "unknown
// provenance," gated the same as a filter recorded for a different event.
// `.local()` + `keepOnClear` for the same reason as `typeFilter` itself —
// it names a property of the door position, not of who's signed in.
export const typeFilterEventId = state<string | null>(
	"scanner/type-filter-event",
).local(null, { keepOnClear: true });

// When the filter above was set — the epoch instant an operator took this
// door off automatic, recorded rather than derived, because nothing else the
// door holds still says when a set was picked once the moment has passed. An
// instant and not a day: the lane the derivation below answers with changes at
// entry-window boundaries, so a door has no day to date a pick against. `null`
// is no stamp at all, never a stale one — a door already standing on a
// hand-picked filter from before this namespace existed loads back at its own
// default, a namespace never written being never restored.
//
// Restore validation is always on, but its default authority is `initial`'s
// own runtime shape, and an `initial` of `null` has none — the stored value
// comes back verbatim, unvalidated and unwarned. `typeFilterEventId` above
// carries that same hole harmlessly: it is only ever compared, never shown,
// and a value that is not a real event id fails the comparison, which is the
// open door. Nothing here fails safe the same way — a restored string reaches
// a date formatter and renders `Invalid Date` at the door — so what keeps a
// non-instant out of an instant is the parse.
export const typeFilterPickedAt = state<number | null>(
	"scanner/type-filter-picked-at",
).local(null, {
	keepOnClear: true,
	schema: z.number().int().nullable(),
});

/** The catalog as the two functions below read it: which event it was read
 *  for, and each type's own entry window — narrowed to the fields that decide
 *  admission, so a caller holding a fuller catalog passes it unchanged. */
type EntryWindowCatalog = {
	readonly eventId: string | null;
	readonly byId: ReadonlyMap<
		string,
		Pick<TicketType.TicketType, "entryWindow">
	>;
};

/** The event a door stands on, reduced to the two facts admission turns on:
 *  which one it is, and the schedule an absent entry window inherits. They
 *  ride one value so a caller cannot compose one event's id with another's
 *  schedule by accident — that pairing stays writable, it just takes saying
 *  so. */
type AdmittingEvent = Pick<Event.Event, "schedule"> & { readonly id: string };

/**
 * The set a door admits before an operator has narrowed it: every type whose
 * entry window contains THIS INSTANT. A nine-day festival's door stands on the
 * types that are valid to scan right now instead of on all nine nights at
 * once, and it does so derived rather than written, so the set follows the
 * catalog and the clock instead of freezing whatever the index held the first
 * time somebody opened the panel.
 *
 * An instant, and never a calendar day. Midnight is not a door event; a window
 * opening or closing is. Under a day-shaped lane a club night running
 * 22:00→04:00 has the next day's programme join it at 00:00 while that night
 * is still in full flow, and a type valid 18:00–23:00 sits in it from
 * midnight — nine hours before it admits anybody. Window bounds and `now` are
 * both epoch millis, so nothing here projects one onto the other and no zone
 * enters the derivation: the venue's zone decides how a day is LABELLED, never
 * when a door opens.
 *
 * One rule, containment, and no second clause for a type tied to no window of
 * its own: an absent entryWindow inherits the event's own schedule, so such a
 * type is open for the whole run and this instant is already inside it. Both
 * bounds are inclusive, the same reading `resolveEntryWindow`'s own consumers
 * take — the door is open at the instant a window closes, which is the whole
 * question being asked.
 *
 * Every path that narrows nothing returns `[]`, which reads as accept
 * EVERYTHING — the polarity this whole file exists to get right, and the
 * reason nothing here is written as a guard that could be dropped without the
 * answer changing. It is reached by an unresolved event document, by a catalog
 * carrying no stamp for the event now selected, by an empty catalog, and by an
 * instant outside every window the catalog holds — a door standing before the
 * first window opens or after the last one closes, which has nothing to say
 * yet rather than a day's worth of types to assert nine hours early.
 *
 * The stamp is the load-bearing one, and it is a comparison rather than a
 * count for a reason emptiness cannot cover: ticket-types clears its index only
 * when the event id goes null, never on a switch from one event to another, so
 * the map can still hold the PREVIOUS event's types while a new event is
 * selected — and a type among them whose entryWindow is absent inherits the NEW
 * event's schedule, therefore spans that whole run, therefore contains this
 * instant. The set that comes back is then a non-empty allow-list of the
 * previous event's auto-ids, matching nothing and refusing every guest. Asking
 * the catalog which event it was read for is the only question that separates
 * that set from a real lane; asking whether it is empty does not.
 *
 * A non-finite bound needs no guard of its own, unlike in
 * `nextEntryWindowBoundary` below. `-Infinity` and `NaN` are both
 * representable — the schema asks only that a window close after it opens —
 * and each simply passes or fails the two comparisons: a window unbounded on
 * the side this instant sits inside genuinely contains it, and every other
 * non-finite shape falls out.
 *
 * Fully parameterised, never reading the singletons above, for the reason
 * `isTicketAccepted` below is: the caller decides what one reading is, and the
 * band that renders this set resolves the same answer from the rows it was
 * handed rather than from a second read of its own.
 */
export function defaultAcceptedTypeIds(
	catalog: EntryWindowCatalog,
	selectedEvent: AdmittingEvent | null,
	now: number,
): readonly string[] {
	if (selectedEvent === null) return [];
	if (catalog.eventId !== selectedEvent.id) return [];
	return [...catalog.byId]
		.filter(([, type]) => {
			// The one reading of when a type admits: an absent entryWindow inherits
			// the event's own schedule, which is what keeps such a type open for
			// the whole run rather than for none of it.
			const window = TicketType.resolveEntryWindow(type, selectedEvent);
			return (
				window.opensAt.toMillis() <= now && now <= window.closesAt.toMillis()
			);
		})
		.map(([id]) => id);
}

/**
 * The next instant at which `defaultAcceptedTypeIds` above answers differently
 * — the nearest entry-window edge this catalog holds after `after`, or `null`
 * when it holds none. The door's own clock is armed against it, so every
 * wake-up is a real door event: a window opening or closing, never an
 * arbitrary hour.
 *
 * An opening edge is `opensAt` itself, the first instant a window contains.
 * A closing edge is the millisecond AFTER `closesAt`, because containment is
 * inclusive on both bounds — at `closesAt` the door is still open, and arming
 * there would wake the phone to re-derive the same answer and then not wake it
 * again until some other type's edge.
 *
 * Non-finite bounds ARE dropped here, unlike in the derivation above: an
 * unbounded edge is not an instant a timer can be set for, and it is not one
 * the answer changes at either.
 *
 * The same event stamp gates it, so a catalog holding the previous event's
 * types arms nothing — its windows decide no admission, so their edges are not
 * door events.
 *
 * Parameterised for the reason its neighbours are: the reconciler that arms
 * the timer holds the reading, and this answers about that one reading.
 */
export function nextEntryWindowBoundary(
	catalog: EntryWindowCatalog,
	selectedEvent: AdmittingEvent | null,
	after: number,
): number | null {
	if (selectedEvent === null) return null;
	if (catalog.eventId !== selectedEvent.id) return null;
	return (
		[...catalog.byId.values()]
			.flatMap((type) => {
				const window = TicketType.resolveEntryWindow(type, selectedEvent);
				return [window.opensAt.toMillis(), window.closesAt.toMillis() + 1];
			})
			.filter((edge) => Number.isFinite(edge) && edge > after)
			.sort((a, b) => a - b)
			.at(0) ?? null
	);
}

/** Cold clock shared by the scanner's boot and the console's mounted sync.
 *  Each owner writes todayInstant and owns cancellation. Midnight refreshes
 *  calendar labels; only entry-window containment determines admission. */
export const checkinClock$ = defer(() => {
	return combineLatest([
		ticketTypesIndex.$,
		eventDetailsById.$,
		selectedEventId.$,
		venueTimezone.$,
	]).pipe(
		switchMap(([catalog, details, eventId, timezone]) =>
			defer(() => {
				const now = Date.now();
				const event = eventId === null ? null : (details.get(eventId) ?? null);
				const boundary = nextEntryWindowBoundary(
					catalog,
					eventId === null || event === null
						? null
						: { id: eventId, schedule: event.schedule },
					now,
				);
				const day = TicketType.dayKeyOf(now, timezone);
				let before = now;
				let midnight = now + 48 * 60 * 60_000;
				// Find the first instant on the next venue day, including 23/25-hour
				// DST days. This also bounds every timer below the signed 32-bit limit
				// even when the next ticket window is months away.
				while (midnight - before > 1) {
					const middle = Math.floor((before + midnight) / 2);
					if (TicketType.dayKeyOf(middle, timezone) <= day) before = middle;
					else midnight = middle;
				}
				return timer(Math.min(boundary ?? midnight, midnight) - now);
			}).pipe(repeat(), startWith(null)),
		),
		map(() => Date.now()),
	);
});

// Whether the recorded filter applies to the door as it now stands: it names
// an event, and that event is the one selected. Total over both nullables,
// because a recorded event of `null` means "no filter was ever recorded for
// this" and must read that way against every selection including no selection
// at all — four writers point the door at no event (sign-out, a persisted id
// naming an event that no longer exists, and the console on both its teardown
// and its no-event leg), and the console mounts the scan surface with no event
// guard, so `(null, null)` is a state a real door stands in while a real scan
// resolves. A plain `===` reads that pair as a match and hands back whatever
// `typeFilter` still holds — the previous event's ids, enforced as an
// allow-list matching nothing.
//
// One predicate, read by both the set and the mode below, so the door cannot
// enforce one answer while it says the other.
const filterApplies = () =>
	typeFilterEventId.value !== null &&
	typeFilterEventId.value === selectedEventId.value;

/**
 * `typeFilter`, gated to the event it was actually set for. Every reader of
 * the accepted-types filter — the verdict pipeline, the door and console
 * scan counts, the manual-lookup chip row — reads this, never `typeFilter`
 * itself: nothing resets `typeFilter` when the operator switches events, so
 * it stands with the PREVIOUS event's typeIds, and enforcing those against
 * the new event's catalog is an allow-list matching nothing, rejecting
 * every guest at the door. A filter whose recorded event doesn't match the
 * one now selected — including a filter with no recorded event at all,
 * i.e. `typeFilterEventId` still at its default — falls through to
 * `defaultAcceptedTypeIds` above, which answers the lane valid right now where
 * it can and the same open `[]` a fresh install starts in where it cannot.
 *
 * The fall-through is one-way on purpose: the moment an operator records a
 * filter for the event now selected, the derivation stops and their set
 * stands until the event changes.
 */
export const acceptedTypeIds = computed<readonly string[]>(() => {
	if (filterApplies()) return typeFilter.value;
	const eventId = selectedEventId.value;
	const event =
		eventId === null ? null : (eventDetailsById.value.get(eventId) ?? null);
	return defaultAcceptedTypeIds(
		ticketTypesIndex.value,
		eventId === null || event === null
			? null
			: { id: eventId, schedule: event.schedule },
		todayInstant.value,
	);
});

/** Whether the door is deriving its own lane rather than enforcing a set an
 *  operator recorded — the same reading `acceptedTypeIds` above makes, not a
 *  second cell recording it. A stored mode is a second representation of one
 *  fact, free to say "the operator's set" while the gate has already fallen
 *  back to the derivation, which is exactly the disagreement an operator
 *  cannot see from the chips. It says nothing about how wide the door is:
 *  either mode reaches `[]`, and `[]` admits every type. */
export const typeFilterIsAutomatic = computed(() => !filterApplies());

/** The instant the set this door is enforcing was picked, or `null` — the
 *  stamp gated through the same predicate the set and the mode read, for the
 *  reason `acceptedTypeIds` is gated: `typeFilterPickedAt` is a single slot
 *  that keeps the last instant written to it across an event switch and a
 *  sign-out, so a surface reading it raw would date a set the door is not
 *  enforcing. Null in automatic mode is not a missing stamp — a derived lane
 *  was picked at no instant at all. */
export const manualPickedAt = computed(() =>
	filterApplies() ? typeFilterPickedAt.value : null,
);

// Which provenance THIS entrance admits — a guest lane at a venue that runs a
// separate guest entrance. A scalar, not a set like `typeFilter`, for two
// reasons. Its values are a closed global vocabulary rather than one event's
// own auto-ids, so nothing can go stale against another event and no
// `...EventId` companion is needed. And a set of exactly two members renders
// four states over three meanings — neither selected and both selected would
// both have to mean "admit everything" — on the one control whose polarity
// this file exists to get right. A restored disk value is safe for the same
// reason: schmancy validates an Array node only by `Array.isArray`, so a set
// here would restore `["Guest"]` verbatim and, under allow-list polarity,
// refuse every ticket at the door.
export const originFilter = state<OriginFilter>("scanner/origin-filter").local(
	"all",
	{ keepOnClear: true },
);

/** Both axes must pass. Empty `acceptedTypeIds` accepts every type and an
 *  `all` origin filter accepts every provenance — get that polarity backwards
 *  and a fresh install rejects every ticket instead of being open by default;
 *  it is the one rule the whole settings surface exists to get right.
 *
 *  Fully parameterised, never reading the singletons above: `verdict.sync.ts`
 *  snapshots both filters into its own `ResolveContext` so one scan resolves
 *  against one point-in-time reading, and a predicate that re-read the live
 *  state would silently defeat that snapshot.
 *
 *  A ticket with no `origin` counts as `sale` — the provenance every ticket
 *  minted before that field existed actually has, absent a backfill. */
export function isTicketAccepted(
	ticket: Pick<Ticket.Ticket, "typeId" | "origin">,
	acceptedTypeIds: readonly string[],
	originFilter: OriginFilter,
): boolean {
	if (acceptedTypeIds.length > 0 && !acceptedTypeIds.includes(ticket.typeId)) {
		return false;
	}
	return originFilter === "all" || (ticket.origin ?? "sale") === originFilter;
}

/** Which mode this scan resolves under, once the scanned type's own door
 *  policy is folded into the device's setting. A type flagged `verifyAtDoor`
 *  is admitted by a person, so it withholds the write behind an explicit tap
 *  no matter what the device is set to; `manual` is also the answer whenever
 *  the catalog cannot say.
 *
 *  Fully parameterised for the same reason `isTicketAccepted` above is:
 *  `verdict.sync.ts` snapshots the catalog into its own `ResolveContext` so
 *  one scan resolves against one point-in-time reading, and a function that
 *  re-read the live singleton would silently defeat that snapshot.
 *
 *  An unknown typeId resolves to `manual` rather than `auto` — the polarity
 *  this file's other filters get right in the opposite direction, because
 *  here the safe answer is to ask a person. The catalog is not a complete
 *  record of which types hold: `ticket-types.sync.ts` clears its index only
 *  when the event id goes null, never on a switch from one event to another,
 *  so the map can hold the PREVIOUS event's ids while this scan carries the
 *  new event's; and that same reader `safeParse`-skips any type document that
 *  will not parse. A positive allow-list of ids-to-hold would fail OPEN for
 *  exactly those two cases — admitting a press ticket on decode because its
 *  type was missing from the map — so absence refuses instead. */
export function resolveCheckinMode(
	deviceMode: CheckinMode,
	typeId: string,
	catalog: ReadonlyMap<
		string,
		Pick<TicketType.TicketType, "verifyAtDoor">
	> | null,
): CheckinMode {
	if (deviceMode === "manual") return "manual";
	if (catalog === null) return "manual";
	const type = catalog.get(typeId);
	if (!type) return "manual";
	return type.verifyAtDoor ? "manual" : "auto";
}
